export type PromptTestCase = {
    text: string
    target: 'Yes' | 'No'
}

export type PromptResult = {
    text: string
    target: 'Yes' | 'No'
    score: number | null
    justification: string
}

export type Scoreable = {
    target: 'Yes' | 'No'
    score: number | null
}

export function parseMessages(messages: string): PromptTestCase[] | undefined {
    try {
        const lines = messages.split('\n')
        const testCases: PromptTestCase[] = []
        for (const line of lines) {
            const trimmedLine = line.trim()

            if (trimmedLine) {
                const lowerLine = trimmedLine.toLowerCase()
                let target: 'Yes' | 'No' | null = null
                let targetLength = 0

                // Handle both quoted ("Yes"/"No") and unquoted (Yes/No) targets
                if (lowerLine.endsWith('"yes"')) {
                    target = 'Yes'
                    targetLength = 5
                } else if (lowerLine.endsWith('"no"')) {
                    target = 'No'
                    targetLength = 4
                } else if (lowerLine.endsWith('yes')) {
                    target = 'Yes'
                    targetLength = 3
                } else if (lowerLine.endsWith('no')) {
                    target = 'No'
                    targetLength = 2
                }

                // line is ignored if it doesn't end in yes or no.
                if (!target) continue

                // this will remove the yes/no, the delimiter and any quotes.
                let text = trimmedLine.slice(0, -targetLength).trimEnd()
                if (text.endsWith(',') || text.endsWith('\t')) {
                    text = text.slice(0, -1).trimEnd()
                }
                if (text.startsWith('"') && text.endsWith('"')) {
                    text = text.slice(1, -1)
                }

                if (text) {
                    testCases.push({ text, target })
                }
            }
        }
        return testCases
    } catch (error) {
        return undefined
    }
}

export function evaluateMetrics(
    testResults: Scoreable[],
    moderationBoundary: number = 0.75
): {
    accuracy: number
    precision: number
    recall: number
    f1: number
} {
    let truePositive = 0
    let trueNegative = 0
    let falsePositive = 0
    let falseNegative = 0

    // Loop through test results and classify
    // Use score >= moderationBoundary to determine classification
    // Skip entries with null scores (API failures)
    for (const entry of testResults) {
        if (entry.score === null) continue

        const target = entry.target.toLowerCase()
        const result = entry.score >= moderationBoundary

        if (target === 'yes' && result) {
            truePositive++
        } else if (target === 'no' && !result) {
            trueNegative++
        } else if (target === 'no' && result) {
            falsePositive++
        } else if (target === 'yes' && !result) {
            falseNegative++
        }
    }

    // Calculate accuracy (only count results with valid scores)
    const validResults = truePositive + trueNegative + falsePositive + falseNegative
    const accuracy = validResults > 0 ? (truePositive + trueNegative) / validResults : 0

    // Calculate precision (handle division by zero)
    const precision =
        truePositive + falsePositive > 0 ? truePositive / (truePositive + falsePositive) : 0

    // Calculate recall (handle division by zero)
    const recall =
        truePositive + falseNegative > 0 ? truePositive / (truePositive + falseNegative) : 0

    // Calculate F1 score (harmonic mean of precision and recall).
    // See here for more details on how this score is calcualted: https://en.wikipedia.org/wiki/F-score
    const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0

    return {
        accuracy: Math.round(accuracy * 100),
        precision: Math.round(precision * 100),
        recall: Math.round(recall * 100),
        f1: Math.round(f1 * 100)
    }
}
